export const VIDEO_FILE_NAME_RE = /\.(mp4|avi|mov|wmv|flv|mkv|m4v|webm)$/i;
export const OPTIMISTIC_PUBLISHING_TTL_MS = 2 * 60 * 1000;

export const isVideoFileName = (name) => VIDEO_FILE_NAME_RE.test(String(name || '').trim());

export const sanitizeDeliveryPlatforms = (platforms, deliveryPlatformValueSet) => {
  if (!Array.isArray(platforms)) return [];
  const out = [];
  for (const p of platforms) {
    const v = String(p || '').trim();
    if (!v || !deliveryPlatformValueSet?.has(v)) continue;
    if (!out.includes(v)) out.push(v);
  }
  return out;
};

export const sameArray = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const normalizeContentType = (raw) => {
  const t = String(raw || '').trim();
  if (!t) return '';
  const normalized = t.toLowerCase();
  if (normalized === 'video') return '视频';
  if (normalized === 'image_text' || normalized === 'imagetext' || normalized === 'article') return '图文';
  return t;
};

export const isVideoType = (value) => normalizeContentType(value) === '视频';

export const getUrlPathname = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw, window.location.origin).pathname || raw;
  } catch (_) {
    return raw;
  }
};

export const isVideoUrl = (url) => /\.(mp4|avi|mov|wmv|flv|webm|m3u8)$/i.test(getUrlPathname(url));

export const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(getUrlPathname(url));

export const mapContentListItem = (item) => ({
  ...item,
  category_level1: item.primary_category,
  category_level2: item.secondary_category,
  risk_flag: item.risk_flag,
  manual_review_required: item.manual_review_required,
  is_manual_reviewed: item.is_manual_reviewed ?? false,
});

export const applyOptimisticPublishingToItems = ({
  items = [],
  optimisticPublishingByContentId = {},
  now = Date.now(),
  optimisticTtlMs = OPTIMISTIC_PUBLISHING_TTL_MS,
} = {}) => {
  const idsToClear = [];
  const nextItems = (items || []).map((item) => {
    const contentId = item?.content_id;
    const optimisticTs = optimisticPublishingByContentId?.[contentId];
    if (!optimisticTs) return item;
    if (now - optimisticTs < optimisticTtlMs) {
      if (item?.status === '待发布') return { ...item, status: '发布中' };
      idsToClear.push(contentId);
      return item;
    }
    idsToClear.push(contentId);
    return item;
  });

  return {
    items: nextItems,
    idsToClear,
  };
};

export const buildContentSearchParams = ({ values = {}, page = 1, pageSize = 10 } = {}) => {
  const {
    tags,
    ingested_at_range,
    content_id,
    creator_user_id,
    author,
    content_type,
    status,
    source,
    available_platforms,
    risk_flag,
    manual_review_required,
    is_manual_reviewed,
    title,
    keyword,
  } = values;

  const filters = {};

  if (content_type !== undefined && content_type !== null) {
    filters.content_type = Array.isArray(content_type) ? content_type : [content_type];
  }
  if (creator_user_id) filters.creator_user_id = creator_user_id;
  if (author) filters.author = author;
  if (status !== undefined && status !== null) {
    filters.status = Array.isArray(status) ? status : [status];
  }
  if (source) filters.source = source;
  if (title) filters.title = String(title).trim();
  if (content_id) filters.content_id = String(content_id).trim();
  if (tags) {
    const tagsArray = String(tags)
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (tagsArray.length > 0) {
      filters.tags = tagsArray;
    }
  }
  if (available_platforms !== undefined && available_platforms !== null) {
    filters.available_platforms = Array.isArray(available_platforms) ? available_platforms : [available_platforms];
  }
  if (risk_flag !== undefined && risk_flag !== null) filters.risk_flag = risk_flag;
  if (manual_review_required !== undefined && manual_review_required !== null) {
    filters.manual_review_required = manual_review_required;
  }
  if (is_manual_reviewed !== undefined && is_manual_reviewed !== null) {
    filters.is_manual_reviewed = is_manual_reviewed;
  }
  if (ingested_at_range) {
    const fromDate = ingested_at_range[0]?.toDate ? ingested_at_range[0].toDate() : ingested_at_range[0];
    const toDate = ingested_at_range[1]?.toDate ? ingested_at_range[1].toDate() : ingested_at_range[1];
    filters.ingested_at_range = {
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
    };
  }

  const searchParams = {
    page,
    page_size: pageSize,
    keyword: keyword ? String(keyword).trim() : '',
    filters: Object.keys(filters).length > 0 ? filters : undefined,
    sort: {
      field: 'ingested_at',
      order: 'desc',
    },
  };

  if (!searchParams.filters) delete searchParams.filters;
  return searchParams;
};
